import { useApi } from "./useApi";
import { ReservationsUrl, ReservationCardUrl } from "../utils/constants";
import { useQuery } from "@tanstack/react-query";

interface IReservationCard {
  totalReservationsCount: number;
  manualReservationsCount: number;
  automaticReservationsCount: number;
  pendingReservationsCount: number;
  cancelledReservationsCount: number;
}
interface IReservations {
  statusCode: number;
  isSuccess: boolean;
  errorMessages: any[];
  totalRecords: number;
  result: {
    reservationDTOs: ReservationDto[];
  };
}

interface ReservationDto {
  id: number;
  reservationId: string;
  propertyId: number;
  propertyName: string;
  checkIn: string;
  checkOut: string;
  guestName: string;
  guestPhoneNumber: string;
  platFormSRC: number;
  channelName: string;
  reservationDate: string;
  propertyLocation: string;
  numberOfNights: number;
  totalPrice: number;
  reservationNumber?: string;
  bookingStatus: string;
  createOn: string;
  modifiedOn: string;
  isMessageSend: boolean;
  ownerId: number;
}

interface ReservationsParams {
  pageSize: string;
  pageNumber: string;
  fromDate?: string;
  toDate?: string;
  minPrice?: number;
  maxPrice?: number;
  channels?: string[];
  search?: string;
  selectedNames?: string[];
}

const useReservations = () => {
  const { apiPrivate } = useApi();

  const ReservationCard = async () => {
    const {
      data: { result },
    } = await apiPrivate.get<{ result: IReservationCard }>(ReservationCardUrl);
    return result;
  };

  const Reservations = async (params: ReservationsParams) => {
    // Build query parameters
    const queryParams: any = {
      pageSize: params.pageSize,
      pageNumber: params.pageNumber,
    };

    // Add optional parameters if they exist
    if (params.fromDate) {
      queryParams.fromDate = params.fromDate;
    }
    if (params.toDate) {
      queryParams.toDate = params.toDate;
    }
    if (params.minPrice !== undefined) {
      queryParams.minPrice = params.minPrice;
    }
    if (params.maxPrice !== undefined) {
      queryParams.maxPrice = params.maxPrice;
    }
    if (params.channels && params.channels.length > 0) {
      // For axios, we need to pass channels as separate parameters
      // This will create: ?channels=1&channels=2&channels=6
      queryParams.channels = params.channels;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.selectedNames && params.selectedNames.length > 0) {
      // For axios, we need to pass selectedNames as separate parameters
      // This will create: ?selectedNames=name1&selectedNames=name2
      queryParams.selectedNames = params.selectedNames;
    }

    const response: { data: IReservations } = await apiPrivate.get(
      ReservationsUrl,
      {
        params: queryParams,
      }
    );
    return response;
  };

  const ReservationCardQuery = () =>
    useQuery({
      queryKey: [ReservationCardUrl],
      queryFn: ReservationCard,
    });

  const ReservationsQuery = (data: ReservationsParams) =>
    useQuery({
      queryKey: [ReservationsUrl, data],
      queryFn: () => Reservations(data),
    });

  return {
    ReservationCardQuery,
    ReservationsQuery,
  };
};
export { useReservations };
